import CustomComponent from '../CustomComponent';
import Request, { ApiMode } from '../../utils/request';
import { startLoadingAnimation, stopLoadingAnimation } from '../../utils/dom/loading';

const REMOVE_BTN_SELECTOR = '.js-edit-card-info-remove';
const REMOVE_CONTACT_ENDPOINT_ATTR = 'data-remove-contact-endpoint';
const DEFAULT_REMOVE_ERROR_MESSAGE = 'Unable to remove contact. Please try again.';
const PETITE_BTN_CLASSES = 'lta-btn lta-btn--xs lta-btn--petite lta-btn--icon lta-btn--icon-before';
export const EDIT_CARD_INFO_REMOVED_EVENT = 'lta-edit-card-info:removed';
export const EDIT_CARD_INFO_REMOVE_START_EVENT = 'lta-edit-card-info:remove-start';
export const EDIT_CARD_INFO_REMOVE_ERROR_EVENT = 'lta-edit-card-info:remove-error';

const spriteIcon = (iconId) =>
	`<svg class="lta-icon js-lta-text-input--btn-icon u-mr-1" width="1.5em" height="1.5em" fill="currentColor" aria-hidden="true">
		<use xlink:href="/static/images/icons/lta-icons.svg#${iconId}"></use>
	</svg>`;

export class EditCardInfoComponent extends CustomComponent {
	static key = 'lta-edit-card-info';

	static get observedAttributes() {
		return [
			'name',
			'role',
			'level',
			'button-label',
			'button-classes',
			'contact-id',
			'classes',
			'show-remove-button',
			'remove-button-label',
			'remove-button-classes',
			'remove-endpoint',
			'remove-icon'
		];
	}

	onLoad() {
		if (this._clickBound) {
			return;
		}

		this._clickBound = true;
		this.addEventListener('click', (event) => this.handleClick(event));
	}

	getCardData() {
		const contactId = this.getAttribute('contact-id') || '';
		const roles = this.getAttribute('role') || '';
		const showRemoveButton = this.getAttribute('show-remove-button');
		const endpoint =
			this.getAttribute('remove-endpoint') ||
			this.closest(`[${REMOVE_CONTACT_ENDPOINT_ATTR}]`)?.getAttribute(REMOVE_CONTACT_ENDPOINT_ATTR) ||
			'';

		return { contactId, roles, showRemoveButton, endpoint };
	}

	shouldShowRemoveButton() {
		const { contactId, showRemoveButton, endpoint } = this.getCardData();

		if (showRemoveButton === 'false' || !contactId) {
			return false;
		}

		return showRemoveButton === 'true' || Boolean(endpoint);
	}

	handleClick(event) {
		const removeButton = event.target?.closest?.(REMOVE_BTN_SELECTOR);
		if (!removeButton || !this.contains(removeButton)) {
			return;
		}

		event.preventDefault();
		event.stopPropagation();
		this.handleRemoveClick(removeButton);
	}

	async handleRemoveClick(button) {
		if (this._removing || button.disabled || button.getAttribute('aria-busy') === 'true') {
			return;
		}

		const { contactId, roles, endpoint } = this.getCardData();
		if (!contactId || !endpoint) {
			return;
		}

		this._removing = true;
		button.setAttribute('aria-busy', 'true');
		button.disabled = true;
		this.dispatchCardEvent(EDIT_CARD_INFO_REMOVE_START_EVENT, { contactId, roles });
		startLoadingAnimation();

		try {
			const response = await Request.post(
				endpoint,
				{
					contactId,
					roles
				},
				{
					req: {
						apiMode: ApiMode.LtaError
					}
				}
			);

			const { Success, success, errorMessage } = response || {};

			if (errorMessage || !(Success || success)) {
				this.resetRemoveButton(button);
				this.dispatchCardEvent(EDIT_CARD_INFO_REMOVE_ERROR_EVENT, {
					contactId,
					roles,
					errorMessage: errorMessage || DEFAULT_REMOVE_ERROR_MESSAGE
				});
				return;
			}

			this.dispatchCardEvent(EDIT_CARD_INFO_REMOVED_EVENT, { contactId, roles });
		} catch (error) {
			this.resetRemoveButton(button);
			this.dispatchCardEvent(EDIT_CARD_INFO_REMOVE_ERROR_EVENT, {
				contactId,
				roles,
				errorMessage: this.getRemoveErrorMessage(error)
			});
			// eslint-disable-next-line no-console
			console.error('EditCardInfo – remove contact error:', error?.message || error);
		} finally {
			stopLoadingAnimation();
		}
	}

	dispatchCardEvent(eventName, detail = {}) {
		this.dispatchEvent(
			new CustomEvent(eventName, {
				bubbles: true,
				composed: true,
				detail
			})
		);
	}

	getRemoveErrorMessage(error) {
		return error?.errorMessage || error?.error?.errorMessage || DEFAULT_REMOVE_ERROR_MESSAGE;
	}

	resetRemoveButton(button) {
		this._removing = false;
		button.removeAttribute('aria-busy');
		button.disabled = false;
	}

	render(childrenHTML) {
		const {
			name = '',
			role = '',
			level = '',
			buttonLabel = 'Edit details',
			buttonClasses = '',
			contactId = '',
			classes = '',
			removeButtonLabel = 'Remove contact',
			removeButtonClasses = '',
			removeIcon = 'trash-black'
		} = this.props;

		const slotContent = this._childrenHTML || childrenHTML || '';
		const classNames = ['lta-edit-card-info', classes].filter(Boolean).join(' ');
		const editButtonClasses = [PETITE_BTN_CLASSES, buttonClasses].filter(Boolean).join(' ');
		const removeButtonClassNames = [PETITE_BTN_CLASSES, 'js-edit-card-info-remove', removeButtonClasses]
			.filter(Boolean)
			.join(' ');

		const roleEl = role ? `<p class="lta-edit-card-info__role u-pt-1">${role}</p>` : '';
		const headerEl = `
			<div class="lta-edit-card-info__header">
				${name ? `<p class="lta-edit-card-info__name">${name}</p>` : ''}
				${
					level
						? `<div class="lta-edit-card-info__role-row">
								${roleEl}
								<p class="lta-edit-card-info__level u-pt-1">${level}</p>
							</div>`
						: roleEl
				}
			</div>
		`;

		const removeButtonMarkup = this.shouldShowRemoveButton()
			? `<button type="button" class="${removeButtonClassNames}" data-contact-id="${contactId}" data-roles="${role}">
					${spriteIcon(removeIcon)}${removeButtonLabel}
				</button>`
			: '';

		return `
			<div class="${classNames}">
				${headerEl}
				${slotContent ? `<div class="lta-edit-card-info__body">${slotContent}</div>` : ''}
				<div class="lta-edit-card-info__actions">
					<button type="button" class="${editButtonClasses}"${contactId ? ` data-contact-id="${contactId}"` : ''}>
						${spriteIcon('pencil-fill')}${buttonLabel}
					</button>
					${removeButtonMarkup}
				</div>
			</div>
		`;
	}
}

export default EditCardInfoComponent;
